import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send httpOnly cookies with requests
  timeout: 900000, // 15 minute timeout for large data imports and long-running API calls
});

// Refresh token lock — prevents N parallel refresh calls when JWT expires mid-load.
// The first 401 triggers the refresh; subsequent 401s wait for the same promise.
let refreshPromise: Promise<void> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest) {
      try {
        // If a refresh is already in flight, wait for it instead of issuing another
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API_BASE_URL}/api/v1/auth/refresh`, {}, { withCredentials: true })
            .then(() => {
              // Cookie is set automatically by the response — nothing to store
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        await refreshPromise;
        // Retry the original request (new cookie will be sent automatically)
        return apiClient(originalRequest);
      } catch {
        useAuthStore.getState().logout();
        const onAuthPage = /^\/(login|signup|forgot-password|reset-password|verify-email|sso\/complete)/.test(
          window.location.pathname
        );
        if (!onAuthPage) {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
